import { CollaboratorContractTypeEnum } from "../../constants/AppConstants";
import { Role } from "../AppTypes";
import { BatchOperation, PaginatedBody } from "./generic.type";

export type OptionItem = {
  value: number;
  label: string;
};

export type CMConfig = {
  paths: PathConfigItem[];
  grades: GradeConfigItem[];
  pathGrades: PathGradeConfigItem[];
  subgrades: SubgradeConfigItem[];
  jobFamilies: JobFamilyConfigItem[];
  goalCategories: GoalsCategoryConfigItem[];
  softSkillsDimensions: SoftSkillsDimensionConfigItem[];
  softSkillsLevels: SoftSkillsLevelConfigItem[];
  finalInterviewDimensions: FinalInterviewDimensionConfigItem[];
  feedbacksEvaluation: FeedbacksEvaluationConfigItem[];
  developments: DevelopmentGradeConfigItem[];
};

export enum ConfigStatusEnum {
  IDLE = "IDLE",
  RUNNABLE = "RUNNABLE",
  RUNNING = "RUNNING",
  COMPLETED = "COMPLETED",
  IN_ERROR = "IN_ERROR",
}

export type ConfigItem = {
  year: number;
  confirmed: boolean;
  current: boolean;
  status: ConfigStatusEnum;
  config: CMConfig;
};

export type GetConfigResponse = ConfigItem[];

export type JobFamilyConfigItem = {
  id: number;
  name: string;
  initials: string;
  position: number;
};

export type GetConfigJobFamiliesResponse = JobFamilyConfigItem[];

export type UpdateJobFamiliesConfig = BatchOperation<JobFamilyConfigItem>[];

export type SubgradeConfigItem = {
  id: number;
  name: string;
  position: number;
};

export type GetConfigSubgradesResponse = SubgradeConfigItem[];
export type UpdateSubgradesConfig = BatchOperation<SubgradeConfigItem>[];

export type SoftSkillsDimensionConfigItem = {
  id: number;
  name: string;
  description: string;
  position: number;
};

export type GetConfigSoftSkillsDimensionsResponse =
  SoftSkillsDimensionConfigItem[];
export type UpdateSoftSkillsDimensionsConfig =
  BatchOperation<SoftSkillsDimensionConfigItem>[];

export type SoftSkillsLevelConfigItem = {
  id: number;
  name: string;
  description: string;
  position: number;
};

export type GetConfigSoftSkillsLevelsResponse = SoftSkillsLevelConfigItem[];
export type UpdateSoftSkillsLevelsConfig =
  BatchOperation<SoftSkillsLevelConfigItem>[];

export type GoalsCategoryConfigItem = {
  id: number;
  name: string;
  position: number;
};

export type GetConfigGoalsCategoriesResponse = GoalsCategoryConfigItem[];
export type UpdateGoalsCategoriesConfig =
  BatchOperation<GoalsCategoryConfigItem>[];

export type FeedbacksEvaluationConfigItem = {
  id: number;
  description: string;
  stars: number;
};

export type GetConfigFeedbacksEvaluationsResponse =
  FeedbacksEvaluationConfigItem[];
export type UpdateFeedbacksEvaluationsConfig =
  BatchOperation<FeedbacksEvaluationConfigItem>[];

export type FinalInterviewDimensionConfigItem = {
  id: number;
  position: number;
  initials: string;
  name: string;
  range: number[];
  description: string;
};

export type GetConfigFinalInterviewDimensionsResponse =
  FinalInterviewDimensionConfigItem[];
export type UpdateFinalInterviewDimensionsConfig =
  BatchOperation<FinalInterviewDimensionConfigItem>[];

export type GradeConfigItem = {
  id: number;
  name: string;
  position: number;
};

export type GetConfigGradesResponse = GradeConfigItem[];
export type UpdateGradesConfigRequest = BatchOperation<GradeConfigItem>[];
export type UpdateGradesConfigResponse = GradeConfigItem[];

export type PathGradeConfigItem = {
  id: number;
  name: string;
  pathId: number;
  gradeId: number;
};

export type GetConfigPathsGradesResponse = PathGradeConfigItem[];
export type UpdatePathsGradesConfig = BatchOperation<PathGradeConfigItem>[];

export type PathConfigItem = {
  id: number;
  name: string;
  position: number;
};

export type GetConfigPathsResponse = PathConfigItem[];
export type UpdatePathsConfigRequest = BatchOperation<PathConfigItem>[];
export type UpdatePathsConfigResponse = PathConfigItem[];

export type ConfigCollaborator = {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  areaId: string;
  practiceId: string | null;
  ccId: string | null;
  startDate: string;
  endDate: string | null;
  role: Role;
  enabledCmSheet: boolean;
  contractType: CollaboratorContractTypeEnum;
  taxIdCode: string;
  validCmSheet: boolean;
  statusCmSheet: string;
  careerManagerFullName: string;
  originalRole: Role;
};

export type GetConfigCollaboratorsResponse = PaginatedBody<ConfigCollaborator>;

export type UpdateConfigCollaboratorRequest = Partial<ConfigCollaborator>;

export type ConfigExternalCollaboratorItem = {
  firstName: string;
  lastName: string;
  taxIdCode: string;
  email: string;
  startDate: string;
  endDate: string | null;
  areaId: string;
  practiceId: string;
  ccId: string;
};

export type AddConfigExternalCollaboratorRequest =
  ConfigExternalCollaboratorItem;

export type UpdateConfigExternalCollaboratorRequest =
  Partial<ConfigExternalCollaboratorItem>;

export type CmSheetsStats = {
  pendingFinalFeedback: number | null;
  readyToSend: number | null;
  finalFeedbackRevisionSent: number | null;
  archived: number | null;
  solicitCount: number | null;
  solicitLastUpdateDate: string | null;
};

export type GetConfigCmSheetsStatsResponse = CmSheetsStats;

export type DevelopmentConfigItem = {
  id: number;
  macroCriterionName: string;
  microCriterionName: string;
  description: string;
  position: number;
};

export type DevelopmentGradeConfigItem = {
  gradeId: number;
  indicators: DevelopmentConfigItem[];
};

export type GetConfigDevelopmentsResponse = DevelopmentGradeConfigItem[];
export type UpdateConfigDevelopmentsResponse =
  BatchOperation<DevelopmentConfigItem>[];
